import {
  DataStatus,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
  Card,
  TextInput,
  NumberInput,
  DatePicker,
} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';

import {
  useGetServiceOrderIntegrationXentryInformationQuery,
  usePatchServiceOrderIntegrationXentryInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {GROUNDING_REASON_OPTIONS} from '../../../../../constants/groundingReasonOptions';
import {
  XentryInformationFormSchema,
  XentryInformationFormType,
} from '../types/XentryInformationFormType';

interface OrderXentryInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  isReadOnly?: boolean;
}

export function OrderXentryInformation(props: OrderXentryInformationProps) {
  const {
    data: serviceOrderXentryInformation,
    isError: isServiceOrderXentryInformationError,
    isLoading: isServiceOrderXentryInformationLoading,
  } = useGetServiceOrderIntegrationXentryInformationQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
    },
    {refetchOnMountOrArgChange: true}
  );
  const [patchServiceOrderIntegrationXentryInformation] =
    usePatchServiceOrderIntegrationXentryInformationMutation();

  const handleSubmit: FormSubmitHandler<XentryInformationFormType> = async (data) =>
    await patchServiceOrderIntegrationXentryInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {
        damageCode: data.damageCode ?? null,
        eskulabDecision: serviceOrderXentryInformation?.eskulabDecision ?? null,
        groundedVehicle: {
          isGrounded: data.isGrounded ?? false,
          groundingReason: data.groundingReason ?? null,
        },
      },
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={isServiceOrderXentryInformationLoading}
      isError={isServiceOrderXentryInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.serviceOrder.labels.xentryIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.serviceOrder.labels.xentryIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<XentryInformationFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={XentryInformationFormSchema}
        defaultValues={{
          damageCode: serviceOrderXentryInformation?.damageCode ?? undefined,
          isGrounded: serviceOrderXentryInformation?.groundedVehicle?.isGrounded ?? false,
          groundingReason:
            serviceOrderXentryInformation?.groundedVehicle?.groundingReason ?? undefined,
        }}
      >
        {(control, formApi) => {
          const isGroundedSectionDisabled =
            props.isReadOnly || !serviceOrderXentryInformation?.groundedVehicle?.isEditable;
          const isGroundedValue = formApi.watch('isGrounded') ?? false;
          return (
            <VStack spacing={4}>
              <Grid columns={4} spacing={4}>
                <FormField
                  control={control}
                  name="damageCode"
                  type="text"
                  label={i18n.t('entity.serviceOrder.labels.damageCode')}
                  maxLength={9}
                  isCounterVisible
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('damageCode', props)}
                />
              </Grid>
              <Card
                title={i18n.t('entity.serviceOrder.labels.eskulabDecision')}
                variant="inlineGrey"
                tooltip={i18n.t('entity.serviceOrder.labels.eskulabDecisionTooltip')}
              >
                <Grid columns={4} spacing={4}>
                  <TextInput
                    label={i18n.t('entity.serviceOrder.labels.authorizationNumber')}
                    value={
                      serviceOrderXentryInformation?.eskulabDecision?.authorizationNumber?.toString() ??
                      null
                    }
                    isDisabled
                  />
                  <TextInput
                    label={i18n.t('entity.serviceOrder.labels.statusCode')}
                    value={serviceOrderXentryInformation?.eskulabDecision?.statusCode ?? null}
                    isDisabled
                  />
                  <DatePicker
                    label={i18n.t('entity.serviceOrder.labels.validityDate')}
                    value={parseDate(serviceOrderXentryInformation?.eskulabDecision?.validityDate)}
                    isDisabled
                  />
                  <NumberInput
                    label={i18n.t('entity.serviceOrder.labels.validityMileage')}
                    value={serviceOrderXentryInformation?.eskulabDecision?.validityMileage ?? null}
                    isDisabled
                    suffix={i18n.t('general.metric.km')}
                  />
                </Grid>
              </Card>
              <Card
                title={i18n.t('entity.serviceOrder.labels.groundedVehicle')}
                variant="inlineGrey"
                tooltip={serviceOrderXentryInformation?.groundedVehicle?.tooltip ?? null}
                control={{
                  isDisabled: isGroundedSectionDisabled,
                  type: 'switch',
                  value: isGroundedValue,
                  onChange: () => {
                    const newValue = !isGroundedValue;
                    formApi.setValue('isGrounded', newValue);
                    if (!newValue) {
                      formApi.setValue('groundingReason', null);
                    }
                  },
                }}
              >
                <Grid columns={2} spacing={4}>
                  <FormField
                    control={control}
                    name="groundingReason"
                    type="choice"
                    options={GROUNDING_REASON_OPTIONS}
                    label={i18n.t('entity.serviceOrder.labels.groundingReason')}
                    placeholder={i18n.t('general.labels.select')}
                    isDisabled={isGroundedSectionDisabled || !isGroundedValue}
                  />
                </Grid>
              </Card>
              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.saveChanges')}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('save', props)}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
